<template>
  <!-- The confirm pages' slider (C1, C2): a gold track with a navy handle the member
       drags to the end to submit. SlideToConfirm.vue is MyGiving's brown one, its
       colours fixed in its own CSS, so the campaign has this one.
       `celebrate` adds the T1 frame's motion: the track fills gold to navy behind
       the handle, the handle glows as it goes, and once `done` the handle turns
       into a heart on a full navy track that reads "Pledge submitted". -->
  <div
    ref="track"
    class="relative w-full h-[52px] rounded-full bg-campaign-accent select-none"
    data-disable-swipe-navigation="true"
  >
    <!-- Sliding, the gold-to-navy is drawn the track's width and uncovered as the
         handle goes; sent, the whole track turns blue under "Pledge submitted". -->
    <div
      v-if="celebrate"
      :class="[
        'absolute inset-y-0 left-0 rounded-full bg-gradient-to-r bg-no-repeat to-campaign-primary',
        done
          ? 'from-campaign-motion-blue'
          : 'from-campaign-accent from-[18%] via-campaign-motion-blue via-[52%]',
        !dragging && 'transition-[width] duration-200 ease-out',
      ]"
      :style="{ width: `${fillWidth}px`, backgroundSize: `${trackWidth}px 100%` }"
      data-test="bf-slide-fill"
    />
    <span
      class="absolute inset-0 flex items-center justify-center pl-[20px] text-[16px] font-bold text-campaign-ink"
      :style="celebrate && { opacity: labelOpacity }"
      aria-hidden="true"
    >
      <slot />
    </span>
    <span
      v-if="celebrate"
      class="absolute inset-0 flex items-center justify-center pr-[20px] text-[16px] font-bold text-campaign-on-primary transition-opacity duration-150"
      :class="done ? 'opacity-100' : 'opacity-0'"
      :aria-hidden="!done"
      data-test="bf-slide-done"
    >
      Pledge submitted
    </span>
    <!-- A real button, so the keyboard can submit it too: Enter or Space click it
         with no pointer, and only that click submits. A tap does not; a drag does. -->
    <button
      ref="handle"
      type="button"
      :aria-label="label"
      :disabled="loading || done"
      :class="[
        'absolute left-[6px] top-[6px] size-[40px] rounded-full touch-none',
        'bg-campaign-primary text-campaign-on-primary flex items-center justify-center',
        !dragging && 'transition-transform duration-200 ease-out',
      ]"
      :style="{ transform: `translateX(${offset}px) scale(${handleScale})` }"
      data-test="bf-slide-handle"
      @touchstart="holdTouch"
      @touchmove="holdTouch"
      @pointerdown="start"
      @pointermove="move"
      @pointerup="end"
      @pointercancel="cancel"
      @click="onClick"
    >
      <span
        v-if="celebrate"
        class="pointer-events-none absolute inset-0 rounded-full shadow-[0_0_0_2px_var(--hogc-campaign-on-primary),0_0_32px_10px_var(--hogc-campaign-accent)]"
        :style="{ opacity: progress }"
        aria-hidden="true"
      />
      <svg
        v-if="celebrate && done"
        class="relative animate-bf-heart-pop motion-reduce:animate-none"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        aria-hidden="true"
        data-test="bf-slide-heart"
      >
        <path
          class="fill-campaign-accent stroke-campaign-on-primary"
          stroke-width="1.6"
          stroke-linejoin="round"
          d="M12 20.5s-7.5-4.6-9.2-9.3C1.7 8.1 3.6 4.5 7.1 4.5c2.1 0 3.6 1.2 4.9 3 1.3-1.8 2.8-3 4.9-3 3.5 0 5.4 3.6 4.3 6.7-1.7 4.7-9.2 9.3-9.2 9.3z"
        />
      </svg>
      <DsSpinner v-else-if="loading" size="sm" />
      <DsIcon v-else name="arrow-right" size="md" class="relative" />
    </button>
  </div>
</template>

<script>
import { Icon as DsIcon, Spinner as DsSpinner } from '@team-hogc/hogc-one-design-system';
import { triggerImpact } from '@/utils/haptics';

// How far along the track counts as slid all the way.
const THRESHOLD = 0.9;
// The handle's inset on each side of the track (left-[6px] above).
const INSET = 6;
// The handle (size-[40px]) and both insets: the fill's width with the handle at the start.
const HANDLE_SPAN = 40 + INSET * 2;

export default {
  name: 'PledgeSlideToSubmit',

  components: { DsIcon, DsSpinner },

  props: {
    // The handle's accessible name; the slot is the text on the track.
    label: {
      type: String,
      required: true,
    },
    // While the pledge is sending: the handle stays at the end with a spinner.
    loading: {
      type: Boolean,
      default: false,
    },
    // The celebration's motion (BF_PLEDGE_CELEBRATION_FLAG); without it, as before.
    celebrate: {
      type: Boolean,
      default: false,
    },
    // Sent and accepted: with `celebrate`, the heart and "Pledge submitted". The
    // handle stays at the end and cannot be slid again.
    done: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['submit'],

  data() {
    return {
      offset: 0,
      dragging: false,
      startX: 0,
      // How far the handle can go, measured once per drag.
      max: 0,
      trackWidth: 0,
    };
  },

  computed: {
    // How far along the handle is, 0 to 1; all the way once sent.
    progress() {
      if (this.done) return 1;
      return this.max > 0 ? Math.min(1, this.offset / this.max) : 0;
    },

    // The fill runs from the track's left edge to the handle's right edge.
    fillWidth() {
      if (this.done) return this.trackWidth;
      return this.offset + HANDLE_SPAN;
    },

    // The track's words fade out by 70% of the way, as the frame draws them.
    labelOpacity() {
      return this.done ? 0 : Math.max(0, 1 - this.progress / 0.7);
    },

    handleScale() {
      return this.celebrate ? 1 + 0.12 * this.progress : 1;
    },
  },

  watch: {
    // Back to the start once a send ends: on failure, so the member can slide again.
    loading(value) {
      if (!value && !this.done) this.offset = 0;
    },
  },

  methods: {
    measure() {
      this.trackWidth = this.$refs.track.clientWidth;
      this.max = this.trackWidth - this.$refs.handle.offsetWidth - INSET * 2;
    },

    start(event) {
      if (this.loading || this.done) return;
      this.measure();
      this.dragging = true;
      this.startX = event.clientX - this.offset;
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },

    move(event) {
      if (!this.dragging) return;
      this.offset = Math.min(this.max, Math.max(0, event.clientX - this.startX));
    },

    end() {
      if (!this.dragging) return;
      this.dragging = false;
      if (this.max > 0 && this.offset >= this.max * THRESHOLD) this.submit();
      else this.offset = 0;
    },

    // Keeps the drag on the handle, as SlideToConfirm.vue does for MyGiving. Without
    // it iOS hands the touch to ion-content's scroll part-way through, which fires
    // pointercancel and snaps the handle back. Pointer events still drive the drag;
    // only the browser's own handling of the touch is stopped.
    holdTouch(event) {
      if (this.loading || this.done) return;
      event.preventDefault();
      event.stopPropagation();
    },

    cancel() {
      this.dragging = false;
      this.offset = 0;
    },

    // `detail` is 0 for a click from the keyboard, and the tap count otherwise.
    onClick(event) {
      if (event.detail !== 0 || this.loading || this.done) return;
      this.measure();
      this.submit();
    },

    // A medium tap on native, as SlideToGive gives on a confirm.
    submit() {
      this.offset = this.max;
      triggerImpact('medium');
      this.$emit('submit');
    },
  },
};
</script>
